/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import { v4 as uuid } from 'uuid';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';
import { browserMetrics as bm3 } from '@atlassian/browser-metrics/lib';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { GlobalPageLoadExperience } from '@atlassian/ufo-experimental/global-page-load-experience';
import { getUserLocation } from '@jira/platform__utils-router/src/common/utils/index.tsx';
import type { State, ResourceMetrics } from '../../types.tsx';

export const DEFAULT_TIMEOUT = 10000;
/**
 * Handles the process of resetting the current page state upon navigation.
 * It marks the performance for resetting the page, starts a new page load with
 * the performance metrics, and traces the transition for analytics. If specific
 * feature flags are enabled, it also aborts and starts a new global page load
 * experience. Finally, it updates the state with a new page UUID, clears the apps,
 * and records the navigation.
 */
export const resetCurrentPage =
	(nextRouteUfoName?: string, nextRouteName?: string): Action<State> =>
	({ getState, setState }) => {
		const currentState = getState();
		const { currentPage } = currentState;
		performance.mark('resetCurrentPage');
		bm3.startPageLoad({
			isInitial: false,
		});
		traceUFOTransition(nextRouteUfoName, nextRouteName);

		GlobalPageLoadExperience.abort({
			force: true,
		});
		GlobalPageLoadExperience.startPageLoad('UNKNOWN', false);

		if (currentPage && currentPage.apps && Object.keys(currentPage.apps).length > 0) {
			setState({
				currentPage: {
					uuid: uuid(),
					apps: {},
					startTime: Date.now(),
					userLocation: '',
				},
				visitedPages: [
					...currentState.visitedPages,
					{
						...currentState.currentPage,
					},
				],
				transitionCheckTimestamp: 0,
			});
		}
	};
/**
 * Updates the state to mark an app's readiness based on a timeout, ensuring the
 * app's load process is monitored and managed efficiently.
 */
export const setTimeoutReadyWithAppId =
	(appId: string, timeoutTime: number = Date.now()): Action<State> =>
	({ getState, setState }) => {
		const currentState = getState();
		const { currentPage } = currentState;
		if (currentPage && !(currentPage.apps[appId] && currentPage.apps[appId].timeoutTime)) {
			const apps = {
				...currentPage.apps,
			};
			if (apps[appId] && apps[appId].expectingTimeout) {
				const updatedPage = {
					...currentPage,
					apps: {
						...apps,
						[appId]: {
							...apps[appId],
							timeoutTime,
							expectingTimeout: 0,
						},
					},
				};
				setState({
					currentPage: updatedPage,
				});
			}
		}
	};
export const setTimeoutReady =
	(
		timeoutTime: number = Date.now(),
	): Action<
		State,
		{
			appId: string;
		}
	> =>
	({ dispatch }, { appId }) => {
		dispatch(setTimeoutReadyWithAppId(appId, timeoutTime));
	};
/**
 * Initiates the tracking of an app's load process by marking its start time and
 * setting up a timeout to manage load expectations.
 */
export const setAppStartWithAppId =
	(appId: string, startTime: number = Date.now()): Action<State> =>
	({ dispatch, getState, setState }) => {
		const currentState = getState();
		const { currentPage } = currentState;
		if (currentPage && !(currentPage.apps[appId] && currentPage.apps[appId].startTime)) {
			const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
				dispatch(setTimeoutReadyWithAppId(appId));
			}, DEFAULT_TIMEOUT);
			const updatedPage = {
				...currentPage,
				userLocation: getUserLocation(),
				apps: {
					...currentPage.apps,
					[appId]: {
						startTime,
						expectingTimeout: timeoutId,
					},
				},
			};
			setState({
				currentPage: updatedPage,
			});
		}
	};
export const setAppStart =
	(
		startTime: number = Date.now(),
	): Action<
		State,
		{
			appId: string;
		}
	> =>
	({ dispatch }, { appId }) => {
		dispatch(setAppStartWithAppId(appId, startTime));
	};
/**
 * Marks an app as ready by recording its load completion, ensuring accurate
 * tracking of its readiness state.
 */
export const setAppReadyWithAppId =
	(appId: string, endTime: number = Date.now()): Action<State> =>
	({ getState, setState }) => {
		const currentState = getState();
		const { currentPage } = currentState;
		if (currentPage && currentPage.apps[appId] && currentPage.apps[appId].endTime === undefined) {
			const apps = {
				...currentPage.apps,
			};
			// @ts-expect-error - TS2769 - No overload matches this call.
			clearTimeout(currentPage.apps[appId].expectingTimeout);
			const updatedPage = {
				...currentPage,
				apps: {
					...apps,
					[appId]: {
						...apps[appId],
						endTime,
						expectingTimeout: 0,
					},
				},
			};
			setState({
				currentPage: updatedPage,
			});
		}
	};
export const setAppReady =
	(
		endTime: number = Date.now(),
	): Action<
		State,
		{
			appId: string;
		}
	> =>
	({ dispatch }, { appId }) => {
		dispatch(setAppReadyWithAppId(appId, endTime));
	};
/**
 * Adjusts the current page state to reflect an error condition in any of its
 * apps, facilitating error handling mechanisms.
 */
export const setAppErrorReady =
	(): Action<State> =>
	({ getState, setState }) => {
		const currentState = getState();
		const { currentPage } = currentState;
		if (currentPage && currentPage.apps && Object.keys(currentPage.apps).length > 0) {
			const updatedPage = {
				...currentPage,
				apps: {
					'error-page': {
						endTime: Date.now(),
					},
				},
			};
			setState({
				currentPage: updatedPage,
			});
		}
	};
export const setResourceMetrics =
	(resourceMetrics: ResourceMetrics): Action<State> =>
	({ getState, setState }) => {
		const currentState = getState();
		setState({
			currentPage: {
				...currentState.currentPage,
				resources: [
					...(currentState.currentPage.resources ? currentState.currentPage.resources : []),
					resourceMetrics,
				],
			},
		});
	};

export type Actions = {
	setAppStart: typeof setAppStart;
	setAppReady: typeof setAppReady;
	setAppStartWithAppId: typeof setAppStartWithAppId;
	setAppReadyWithAppId: typeof setAppReadyWithAppId;
	setAppErrorReady: typeof setAppErrorReady;
	setTimeoutReady: typeof setTimeoutReady;
	setTimeoutReadyWithAppId: typeof setTimeoutReadyWithAppId;
	resetCurrentPage: typeof resetCurrentPage;
	setResourceMetrics: typeof setResourceMetrics;
};
